import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { deskFor } from "@/lib/deskAccents";

const REGIONS = ["Africa", "North America", "Latin America and the Caribbean", "Europe", "Asia", "Middle East", "Oceania", "International or Cross-Border"];

export default function CrimeWatch() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const region = sp.get("region") || "";
  const accent = deskFor("crime");

  useEffect(() => {
    setLoading(true);
    api.get("/public/crime", { params: region ? { region } : {} })
      .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [region]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <title>Global Crime Watch | Be-Apuo Media Intelligence</title>
      <meta name="description" content="Verified reporting on organized crime, corruption, court cases and cross-border crime across the world." />
      <div className={`label-caps ${accent.eyebrow}`}>Global Crime Watch</div>
      <h1 className="mt-2 font-editorial font-black text-4xl sm:text-5xl tracking-tight text-ink">Crime, verified. Presumption of innocence, preserved.</h1>
      <p className="mt-4 text-ink/70 max-w-2xl">We separate what is confirmed from what is alleged, and we never describe anyone as guilty absent a conviction.</p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="crime-region-filters">
        <button onClick={() => setSp({})} className={`text-xs px-3 py-1.5 border transition-colors ${!region ? accent.pillActive : accent.pillIdle}`}>All Regions</button>
        {REGIONS.map((r) => (
          <button key={r} data-testid={`crime-region-${r}`} onClick={() => setSp({ region: r })}
            className={`text-xs px-3 py-1.5 border transition-colors ${region === r ? accent.pillActive : accent.pillIdle}`}>{r}</button>
        ))}
      </div>

      <div className={`mt-10 border-t-2 ${accent.topBorder} pt-8`}>
        {loading ? <p className="text-ink/55">Loading…</p> : items.length === 0 ? (
          <div className="border border-ink/10 bg-mist p-12 text-center"><p className="text-ink/60">No published crime stories in this view yet.</p></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {items.map((s) => (
              <Link key={s.id} to={`/global-crime-watch/${s.slug}`} data-testid={`crime-card-${s.slug}`} className={`group block border-t-2 ${accent.topBorder} pt-4`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`label-caps ${accent.eyebrow}`}>{s.crime_category}</span>
                  <span className="text-[0.6rem] uppercase tracking-wider bg-ink/5 text-ink/70 px-1.5 py-0.5">{s.story_status}</span>
                </div>
                <h4 className="mt-3 font-editorial font-bold text-xl leading-snug text-ink group-hover:underline decoration-2 underline-offset-4">{s.headline}</h4>
                <p className="mt-2 text-sm text-ink/70 line-clamp-2">{s.summary}</p>
                <div className="mt-3 text-xs text-ink/55">{s.country} · {s.geographic_region}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
